import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScanLine, MapPin, PackageSearch, Camera, CameraOff } from "lucide-react";
import { API } from "@/context/AuthContext";
import { StatusPill } from "@/components/GateInRegister";

function Row({ label, value, mono, testId }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="w-36 shrink-0 text-slate-400">{label}</span>
      <span className={`text-slate-800 font-medium break-all ${mono ? "font-mono" : ""}`} data-testid={testId}>{value || "—"}</span>
    </div>
  );
}

// Keyboard-wedge scanners type the QR text and send Enter — the input stays focused for back-to-back scans.
export function ScanLookup() {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const [camOn, setCamOn] = useState(false);
  const [camErr, setCamErr] = useState("");
  const scannerRef = useRef(null);
  const lastRef = useRef({ text: "", at: 0 });
  useEffect(() => { ref.current?.focus(); }, []);

  const lookup = useCallback(async (raw) => {
    const c = (raw ?? code).trim();
    if (!c) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/inventory/scan`, { params: { code: c } });
      setRes(r.data);
      setCode("");
      ref.current?.focus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not read this code"); setRes(null); }
    finally { setBusy(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const stopCamera = useCallback(async () => {
    const sc = scannerRef.current;
    scannerRef.current = null;
    if (sc) { try { await sc.stop(); } catch (_) { /* already stopped */ } try { sc.clear(); } catch (_) { /* noop */ } }
    setCamOn(false);
  }, []);

  const startCamera = async () => {
    setCamErr("");
    if (!navigator.mediaDevices?.getUserMedia) { setCamErr("This browser cannot access a camera. Use HTTPS on a phone/laptop with a camera."); return; }
    try {
      const sc = new Html5Qrcode("scan-camera-view", { verbose: false });
      scannerRef.current = sc;
      setCamOn(true);
      await sc.start({ facingMode: "environment" }, { fps: 8, qrbox: (w, h) => { const s = Math.min(w, h) * 0.7; return { width: s, height: s }; } },
        (text) => {
          const now = Date.now();
          if (text === lastRef.current.text && now - lastRef.current.at < 3000) return; // same label held in view
          lastRef.current = { text, at: now };
          toast.success("QR read — looking up…");
          lookup(text);
        }, () => {});
    } catch (e) {
      setCamErr(String(e?.message || e || "Camera permission denied"));
      await stopCamera();
    }
  };
  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  const it = res?.item || {};
  return (
    <div className="space-y-4" data-testid="scan-lookup">
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Scan a label (or paste its QR text) and press Enter</label>
        <div className="relative">
          <ScanLine className="h-4 w-4 text-sky-700 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input ref={ref} value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup()}
            placeholder="MatDoc:5000024541/2026|Item:1|…   or   GateIn|PO:4300000004/10|…" className="h-11 pl-9 border-slate-300 text-sm font-mono" data-testid="scan-input" autoComplete="off" />
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-2">
          <p className="text-xs text-slate-400">Works with USB / Bluetooth barcode scanners in keyboard mode. {busy && "Looking up…"}</p>
          <Button size="sm" variant={camOn ? "default" : "outline"} onClick={camOn ? stopCamera : startCamera} className={camOn ? "bg-red-600 hover:bg-red-700 ml-auto" : "border-slate-300 text-slate-700 ml-auto"} data-testid="scan-camera-toggle">
            {camOn ? <><CameraOff className="h-4 w-4 mr-1.5" />Stop camera</> : <><Camera className="h-4 w-4 mr-1.5" />Scan with camera</>}
          </Button>
        </div>
        <div className={camOn ? "mt-3" : "hidden"} data-testid="scan-camera-wrap">
          <div id="scan-camera-view" className="mx-auto w-full max-w-sm rounded-lg overflow-hidden border border-slate-300 bg-black [&_video]:w-full" />
          <p className="text-xs text-slate-400 text-center mt-1.5">Point the camera at a label — it reads automatically and keeps scanning for the next one.</p>
        </div>
        {camErr && <p className="text-xs text-red-600 mt-2" data-testid="scan-camera-error">{camErr}</p>}
      </div>

      {res && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="scan-result">
          <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><PackageSearch className="h-5 w-5 text-sky-700" />{res.kind === "gate_in" ? "Gate-In label" : "Material Document label"}</h3>
              {res.piece && <span className="rounded-full bg-slate-900 text-white text-[11px] font-bold px-2 py-0.5" data-testid="scan-piece">Piece {res.piece}</span>}
            </div>
            <Row label="Material" value={res.material} mono testId="scan-material" />
            <Row label="Description" value={it.description} testId="scan-description" />
            {res.kind === "material_document" ? (<>
              <Row label="Material Document" value={res.document ? `${res.document.doc}/${res.document.year} · Item ${it.item_no || "?"} · Mvt ${it.movement_type || "?"}` : ""} mono testId="scan-document" />
              <Row label="Posting Date" value={res.document?.posting_date} />
              {res.po && <Row label="Purchase Order" value={`${res.po.po} / Line ${res.po.item || "—"}`} mono testId="scan-po" />}
              {res.mfg_order && <Row label="Production Order" value={`${res.mfg_order.order} / Line ${res.mfg_order.item || "—"}`} mono />}
              <Row label="Document Qty" value={it.qty != null ? `${it.qty} ${it.unit || ""}` : res.qty} />
            </>) : (<>
              <Row label="Purchase Order" value={`${res.po?.po} / Line ${res.po?.item}`} mono testId="scan-po" />
              <Row label="Supplier" value={res.po?.supplier_name || res.po?.supplier} />
              <Row label="Gate-In Date" value={res.gate_in_date} />
              <Row label="PO Ordered / Received / Open" value={it.ordered_qty != null ? `${it.ordered_qty} / ${it.received_qty} / ${it.open_qty} ${it.unit || ""}` : ""} />
            </>)}
            <Row label="Label Qty" value={res.qty} />
            <Row label="WBS" value={res.wbs_element ? `${res.wbs_element}${res.wbs_description ? ` — ${res.wbs_description}` : ""}` : ""} testId="scan-wbs" />
            <Row label="Plant / Sloc" value={[res.plant, res.storage_location].filter(Boolean).join(" / ")} mono testId="scan-location" />
            {res.kind === "gate_in" && res.gate_ins?.length > 0 && (
              <div className="pt-3 mt-2 border-t border-slate-100">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Gate-In register for this line</p>
                {res.gate_ins.map((g, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-3 text-xs text-slate-700 py-1" data-testid={`scan-gatein-${i}`}>
                    <span>{g.gate_in_date}</span><span className="font-semibold">{g.qty_received} gated</span><span>GRN {g.grn_qty}</span>
                    <span className="font-mono text-slate-400">{(g.grn_docs || []).join(", ")}</span><StatusPill s={g.status} /><span className="text-slate-400">{g.by}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3"><MapPin className="h-4 w-4 text-emerald-700" />Current stock (last SAP sync)</h3>
            {(res.stock || []).length === 0 ? <p className="text-sm text-slate-400" data-testid="scan-stock-empty">No stock rows for this material{res.plant ? ` in plant ${res.plant}` : ""}.</p> : (
              <div className="space-y-1.5" data-testid="scan-stock">
                {res.stock.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1">
                    <span className="font-mono text-slate-700">{s.plant} / {s.storage_location}{s.batch ? ` · ${s.batch}` : ""}</span>
                    <span className="font-semibold tabular-nums text-slate-900">{s.qty} {s.base_unit}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
